import { alpha, createTheme } from '@mui/material/styles';
import { EASE, pop } from '../shared/ui/motion';
import {
  ACCENT_SHADOW,
  BORDER,
  BRAND,
  CARD_RADIUS,
  MINT_GLOW,
  MINT_GLOW_STRONG,
  SHADOW_LG,
  accentGradient,
} from '../shared/ui/tokens';

/**
 * Montserrat is bundled with the application (`main.tsx`); the CSP loads nothing from other
 * origins. Until it has loaded, and in tests, the platform's own UI face stands in.
 */
const FONT_STACK = [
  '"Montserrat Variable"',
  'Montserrat',
  'system-ui',
  '-apple-system',
  '"Segoe UI"',
  'Roboto',
  '"Helvetica Neue"',
  'Arial',
  'sans-serif',
].join(',');

const INK = BRAND.charcoal;
const MUTED = '#4a4d52';
const HOVER = 'rgba(47, 49, 54, 0.06)';
const FOCUS_RING = `0 0 0 3px ${MINT_GLOW_STRONG}`;

const SHADOW_SM = '0 1px 2px rgba(47, 49, 54, 0.04), 0 4px 12px -6px rgba(47, 49, 54, 0.08)';
const SHADOW_FLOAT =
  '0 24px 48px -12px rgba(30, 32, 35, 0.28), 0 8px 16px -8px rgba(30, 32, 35, 0.14)';

const gradient = accentGradient();

/**
 * One visual system for the whole SPA, in the TAQON brand: a light canvas, white rounded cards,
 * charcoal text, mint for the primary action and whatever is selected, sentence-case controls and
 * one motion curve. Pages keep their own logic; they inherit the look from these defaults.
 *
 * WCAG 2.1 AA text contrast (FRD-CORE-041): the brand green used as text (`BRAND.ink`) and the
 * warning orange reach 4.5:1 on white, on the canvas and on the selected tint, and charcoal text
 * on every mint stop does too (pinned by `theme-contrast.test`).
 */
export const theme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: BRAND.ink, contrastText: '#ffffff' },
    warning: { main: '#b24a00' },
    background: { default: '#fafbfc', paper: '#ffffff' },
    text: { primary: INK, secondary: MUTED },
    divider: BORDER,
  },
  shape: { borderRadius: 12 },
  typography: {
    fontFamily: FONT_STACK,
    h4: { fontSize: '1.5rem', fontWeight: 700, letterSpacing: '-0.01em' },
    h5: { fontSize: '1.25rem', fontWeight: 700 },
    h6: { fontSize: '1.0625rem', fontWeight: 650 },
    subtitle1: { fontWeight: 600 },
    button: { textTransform: 'none', fontWeight: 600 },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        '::selection': { backgroundColor: MINT_GLOW_STRONG },
        '@media (prefers-reduced-motion: reduce)': {
          '*, *::before, *::after': {
            animationDuration: '0.01ms !important',
            animationIterationCount: '1 !important',
            transitionDuration: '0.01ms !important',
            scrollBehavior: 'auto !important',
          },
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          transition: `transform 200ms ${EASE}, box-shadow 200ms ${EASE}, background-color 200ms ${EASE}, border-color 200ms ${EASE}, filter 200ms ${EASE}`,
          '&:active': { transform: 'scale(0.97)' },
          '&.Mui-focusVisible': { boxShadow: FOCUS_RING },
          // An icon in a button leans into the action under the pointer.
          '& .MuiButton-endIcon, & .MuiButton-startIcon': {
            transition: `transform 200ms ${EASE}`,
          },
          '&:hover .MuiButton-endIcon': { transform: 'translateX(3px)' },
          '&:hover .MuiButton-startIcon': { transform: 'scale(1.12)' },
          variants: [
            {
              // The brand button, as on taqon.ai: charcoal on the mint gradient.
              props: { variant: 'contained', color: 'primary' },
              style: {
                color: BRAND.onMint,
                backgroundColor: BRAND.mint,
                backgroundImage: gradient,
                boxShadow: ACCENT_SHADOW,
                '&:hover': {
                  backgroundColor: BRAND.mint,
                  transform: 'translateY(-2px)',
                  filter: 'brightness(1.05)',
                  boxShadow: '0 16px 28px -10px rgba(82, 184, 130, 0.8)',
                },
                '&:active': { transform: 'scale(0.97)' },
                '&.Mui-focusVisible': {
                  boxShadow: `${ACCENT_SHADOW}, 0 0 0 3px ${alpha(BRAND.charcoal, 0.35)}`,
                },
                '&.Mui-disabled': { backgroundImage: 'none', boxShadow: 'none' },
              },
            },
            {
              props: { variant: 'outlined' },
              style: {
                backgroundColor: '#ffffff',
                '&:hover': { transform: 'translateY(-1px)', boxShadow: SHADOW_SM },
              },
            },
            {
              props: { variant: 'outlined', color: 'primary' },
              style: {
                color: INK,
                borderColor: alpha(BRAND.charcoal, 0.28),
                '&:hover': { borderColor: BRAND.mintDark, backgroundColor: MINT_GLOW },
              },
            },
          ],
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          transition: `transform 200ms ${EASE}, background-color 200ms ${EASE}`,
          '&:hover': { transform: 'scale(1.08)' },
          '&:active': { transform: 'scale(0.92)' },
          '&.Mui-focusVisible': { boxShadow: FOCUS_RING },
        },
      },
    },
    MuiPaper: {
      defaultProps: { variant: 'outlined' },
      styleOverrides: {
        rounded: { borderRadius: CARD_RADIUS },
        outlined: { borderColor: BORDER, boxShadow: SHADOW_SM },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 20,
          border: 0,
          boxShadow: SHADOW_FLOAT,
          animation: `${pop} 240ms ${EASE} backwards`,
        },
        // A full-screen dialog (a task on a phone) meets the screen edges, so it has no corners.
        paperFullScreen: { borderRadius: 0 },
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          '&:not(.MuiBackdrop-invisible)': {
            backgroundColor: 'rgba(30, 32, 35, 0.5)',
            backdropFilter: 'blur(4px)',
          },
        },
      },
    },
    MuiDrawer: { styleOverrides: { paper: { boxShadow: 'none' } } },
    MuiPopover: {
      styleOverrides: { paper: { borderRadius: 12, boxShadow: SHADOW_LG } },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          margin: '2px 6px',
          transition: `background-color 150ms ${EASE}`,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          borderRadius: 8,
          backgroundColor: BRAND.charcoalDark,
          fontSize: '0.75rem',
          fontWeight: 500,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          transition: `box-shadow 200ms ${EASE}`,
          '& .MuiOutlinedInput-notchedOutline': {
            transition: `border-color 200ms ${EASE}`,
          },
          '&:hover:not(.Mui-disabled):not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
            borderColor: BRAND.mintDark,
          },
          '&.Mui-focused': { boxShadow: `0 0 0 4px ${MINT_GLOW}` },
          '&.Mui-disabled': { backgroundColor: '#f5f6f7' },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: { padding: '8px 16px', borderRadius: CARD_RADIUS },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: 0,
          padding: 8,
          borderRadius: CARD_RADIUS,
          backgroundColor: '#ffffff',
          border: `1px solid ${BORDER}`,
          boxShadow: SHADOW_LG,
        },
        list: { gap: 8 },
        indicator: { display: 'none' },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          minHeight: 40,
          minWidth: 0,
          padding: '10px 16px',
          borderRadius: 12,
          fontSize: '0.875rem',
          fontWeight: 500,
          lineHeight: '20px',
          color: MUTED,
          transition: `color 200ms ${EASE}, background-color 200ms ${EASE}, box-shadow 200ms ${EASE}, transform 200ms ${EASE}`,
          '&:hover': { backgroundColor: HOVER, color: INK },
          '&:active': { transform: 'scale(0.97)' },
          '&.Mui-selected': {
            color: BRAND.onMint,
            fontWeight: 600,
            backgroundImage: gradient,
            boxShadow: ACCENT_SHADOW,
            animation: `${pop} 260ms ${EASE}`,
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontWeight: 600,
          color: MUTED,
          backgroundColor: '#f5f6f7',
          fontSize: '0.8125rem',
        },
        // Montserrat runs wide, so table text is a step smaller than body text.
        root: { borderColor: BORDER, fontSize: '0.8125rem' },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: `background-color 160ms ${EASE}, box-shadow 160ms ${EASE}`,
          '&:last-of-type td': { borderBottom: 0 },
          // The row under the pointer: a mint wash and a mint edge.
          'tbody &:hover': {
            backgroundColor: MINT_GLOW,
            boxShadow: `inset 3px 0 0 ${BRAND.mint}`,
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 600, transition: `transform 160ms ${EASE}, box-shadow 160ms ${EASE}` },
        clickable: { '&:hover': { transform: 'translateY(-1px)', boxShadow: SHADOW_SM } },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          textDecorationColor: alpha(BRAND.ink, 0.35),
          textUnderlineOffset: 3,
          transition: `color 160ms ${EASE}, text-decoration-color 160ms ${EASE}`,
          '&:hover': { textDecorationColor: BRAND.ink },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          '&.Mui-checked': { color: BRAND.mintDark },
          '&.Mui-checked + .MuiSwitch-track': { backgroundColor: BRAND.mint, opacity: 0.6 },
        },
      },
    },
    MuiLinearProgress: { styleOverrides: { root: { borderRadius: 999, height: 6 } } },
    // A disabled field's helper text still explains the field, so it stays readable (7.6:1).
    MuiFormHelperText: {
      styleOverrides: {
        root: ({ theme: t }) => ({ '&.Mui-disabled': { color: t.palette.text.secondary } }),
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          transition: `background-color 180ms ${EASE}, color 180ms ${EASE}, transform 180ms ${EASE}, box-shadow 180ms ${EASE}`,
        },
      },
    },
  },
});
